/** @format */
import { inject } from '@angular/core';
import { StateService, TargetState, TargetStateDef, Transition, TransitionService } from '@uirouter/core';
import { catchError, defaultIfEmpty, filter, firstValueFrom, map, Observable, of, skipWhile } from 'rxjs';
import { AuthService } from '../auth/auth-service';
import { TokenStorageService } from '../auth/token-storage.service';
import { NavigationService } from '../navigation/navigation.service';
import { SessionStorageService } from '../storage/session-storage.service';

export abstract class AuthRouteService {
    authService = inject(AuthService);
    transitionService = inject(TransitionService);
    tokenStorage = inject(TokenStorageService);
    state = inject(StateService);
    sessionStorageService = inject(SessionStorageService);
    navigationService = inject(NavigationService);

    initialize() {
        this.registerRouteTransitionListeners();
        this.registerUserLogoutEvents();
        this.setAnonymousUrls();
    }

    registerRouteTransitionListeners() {
        this.transitionService.onStart({ to: this.loginPageStateName }, transition =>
            firstValueFrom(this.handleLoginPageTransition(transition)),
        );
        this.transitionService.onStart({ to: this.loggedInStatesGlob }, transition =>
            firstValueFrom(this.handleLoggedInTransition(transition)),
        );
    }

    registerUserLogoutEvents() {
        this.tokenStorage.userSessionChanged$.subscribe(() => {
            const { state, params, options } = this.sessionChanged;
            this.state.go(state, params, options);
        });
        this.authService.userLoggedIn$
            .pipe(
                // Ignore values until the user has logged in
                skipWhile(loggedIn => !loggedIn),
                filter(loggedIn => !loggedIn),
            )
            .subscribe(() => {
                const { state, params, options } = this.loggedOut;
                // Tag the post-logout URL so the next bootstrap can skip preauth — we know there is no session.
                const paramsWithLoggedOut = { ...params, loggedOut: true };
                const loginTarget = options?.custom?.loginTarget;
                if (loginTarget) {
                    this.sessionStorageService.setItem('loginTarget', loginTarget);
                }
                // Hard navigation to wipe in-memory state from the previous session.
                this.navigationService.navigate(this.state.href(state, paramsWithLoggedOut));
            });
    }

    setAnonymousUrls() {
        this.authService.setAnonymousUrls(this.anonymousUrls, this.excludedUrls);
    }

    // Re-checks the IdP session on every entry into the login state. A still-valid Shibboleth session
    // re-issues a token via /ori/preauth and forwards the user into the app; a 403 (USER_NOT_FOUND etc.)
    // is picked up by NoSisuAccountRedirectService through errorResponse$ and turned into a hard
    // redirect to the no-sisu-account landing page; 401 (no IdP session) just lands on login.
    handleLoginPageTransition(transition?: Transition): Observable<TargetState> {
        if (this.authService.loggedIn()) {
            const { state, params, options } = this.defaultView;
            return of(this.state.target(state, params, options));
        }
        if (transition?.params()?.loggedOut) {
            // Just-logged-out marker on the URL — don't fire preauth, the still-warm IdP session would
            // immediately re-authenticate the user and defeat the logout.
            return of(undefined);
        }
        return this.authService.refreshAuthToken(true).pipe(
            map(() => {
                if (!this.authService.loggedIn()) {
                    return undefined;
                }
                const { state, params, options } = this.defaultView;
                return this.state.target(state, params, options);
            }),
            defaultIfEmpty(undefined),
            catchError(() => of(undefined)),
        );
    }

    handleLoggedInTransition(transition: Transition): Observable<TargetState> {
        return this.authService.initializeAppAuth().pipe(
            map((loggedIn: boolean) => {
                if (!loggedIn) {
                    return this.state.target(this.loginPage.state);
                }
                if (this.authService.insufficientScope(transition.$to().data?.requiresScope)) {
                    const { state, params, options } = this.unauthorized;
                    return this.state.target(state, params, options);
                }
            }),
        );
    }

    abstract get loginPageStateName(): string;

    abstract get loggedInStatesGlob(): string;

    abstract get defaultView(): TargetStateDef;

    abstract get unauthorized(): TargetStateDef;

    abstract get loggedOut(): TargetStateDef;

    abstract get sessionChanged(): TargetStateDef;

    abstract get loginPage(): TargetStateDef;

    abstract get anonymousUrls(): string[];

    abstract get excludedUrls(): string[];
}
